// Marker API client. All endpoints authenticated via Google Bearer token.
// The token is set at auth time and reused across calls.
//
// URL resolution:
//   - If VITE_API_BASE is set at build/dev time, use it as an absolute
//     prefix (e.g., https://your-tunnel.trycloudflare.com or
//     http://localhost:8001). This is required when frontend and backend
//     live on different origins.
//   - If VITE_API_BASE is unset/empty, fall back to relative /api/* paths
//     (assumes same-origin — works if a reverse proxy fronts both).
//
// Token expiry: Google ID tokens live for 1 hour. On expiry the backend
// returns 401 with "Token expired" in the detail. We give the auth layer
// a chance to silently refresh the token via google.accounts.id and
// retry the request once. If refresh fails, the auth layer signs the
// user out and the retried call surfaces the 401 to the caller.

import type {
  BriefDoneItem, HistoryThreadResponse, HistoryTurn,
  UserContextItem, UserContextKind, UserContextListResponse,
  AskResponse, HistoryResponse, FeedbackRating,
  FeedbackSubmission, AdminFeedbackListResponse,
  AdminFeedbackPatch, FeedbackStatus, MeResponse, BriefResponse,
  BriefVoteBody, BriefVote, BriefRepsResponse,
  ScorecardsResponse, ScorecardDetailResponse,
} from './types'

const API_BASE = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '')

function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

let bearerToken: string | null = null

export function setAuthToken(token: string | null) {
  bearerToken = token
}

// --- Token refresh hook ---
// The auth layer registers a refresher function here. When a request
// 401s with a token-expired error, authFetch will await the shared
// refresh promise (creating one if none is in flight) and retry the
// request once. Returning null from the refresher means the refresh
// failed — the caller signs the user out.
type Refresher = () => Promise<string | null>
let refresher: Refresher | null = null
let refreshInFlight: Promise<string | null> | null = null

export function setTokenRefresher(fn: Refresher | null) {
  refresher = fn
}

function isTokenExpiredError(status: number, detail: string): boolean {
  if (status !== 401) return false
  return /token expired|expired token|invalid google token/i.test(detail)
}

function authHeaders(): HeadersInit {
  if (!bearerToken) {
    throw new Error('Not authenticated — no bearer token')
  }
  return {
    Authorization: `Bearer ${bearerToken}`,
    'Content-Type': 'application/json',
  }
}

async function readDetail(res: Response): Promise<string> {
  try {
    const body = await res.clone().json()
    return String(body.detail || body.error || res.statusText)
  } catch {
    return res.statusText
  }
}

// Wrap fetch with auth headers + one-shot 401 refresh & retry. All
// callers below should go through this instead of raw fetch so they
// benefit from the refresh path.
async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path)
  const doFetch = () => fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  })

  let res = await doFetch()
  if (res.status === 401 && refresher) {
    const detail = await readDetail(res)
    if (isTokenExpiredError(res.status, detail)) {
      // Coalesce concurrent refresh attempts into a single promise so
      // we don't trigger multiple Google prompts on a burst of 401s.
      if (!refreshInFlight) {
        refreshInFlight = (async () => {
          try {
            return await refresher!()
          } finally {
            // Clear regardless of outcome — next 401 starts a fresh attempt.
            refreshInFlight = null
          }
        })()
      }
      const newToken = await refreshInFlight
      if (newToken) {
        // Retry exactly once with the new token.
        res = await doFetch()
      }
    }
  }
  return res
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await readDetail(res)
    throw new Error(`HTTP ${res.status}: ${detail}`)
  }
  return res.json() as Promise<T>
}

export interface PriorTurn {
  question: string
  answer: string
}

export async function askQuestion(
  question: string,
  opts: {
    conversationId?: string
    preferModel?: 'sonnet' | 'opus'
    priorTurns?: PriorTurn[]
    /** Scopes the conversation to one opportunity (see primeOpportunity). */
    opportunityId?: string
    /** A document uploaded via uploadDocument, put in front of the model for this question. */
    documentId?: string
  } = {},
): Promise<AskResponse> {
  const body: Record<string, unknown> = { question }
  if (opts.conversationId) body.conversation_id = opts.conversationId
  if (opts.preferModel) body.prefer_model = opts.preferModel
  if (opts.priorTurns && opts.priorTurns.length > 0) {
    body.prior_turns = opts.priorTurns
  }
  if (opts.opportunityId) body.opportunity_id = opts.opportunityId
  if (opts.documentId) body.document_id = opts.documentId

  const res = await authFetch('/api/ask', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return handleResponse<AskResponse>(res)
}

export type AskStreamEvent =
  | { type: 'start' }
  | { type: 'tool_start'; tool: string; label: string }
  | { type: 'text'; text: string }
  | { type: 'done'; result: AskResponse }
  | { type: 'error'; error: string }

/**
 * Streaming twin of askQuestion.
 *
 * Most of a query's wall clock is the tool loop, during which the plain endpoint
 * sends nothing — so the UI could only show a spinner. This surfaces progress
 * ("Searching transcripts…") and then the answer as it is written.
 *
 * Falls back to the non-streaming endpoint if anything about the stream fails, so a
 * proxy that buffers SSE degrades to today's behaviour rather than breaking chat.
 */
export async function askQuestionStreaming(
  question: string,
  onEvent: (e: AskStreamEvent) => void,
  opts: {
    conversationId?: string
    preferModel?: 'sonnet' | 'opus'
    priorTurns?: PriorTurn[]
    /** Scopes the conversation to one opportunity (see primeOpportunity). */
    opportunityId?: string
    /** A document uploaded via uploadDocument, put in front of the model for this question. */
    documentId?: string
  } = {},
): Promise<AskResponse> {
  const body: Record<string, unknown> = { question }
  if (opts.conversationId) body.conversation_id = opts.conversationId
  if (opts.preferModel) body.prefer_model = opts.preferModel
  if (opts.priorTurns && opts.priorTurns.length > 0) body.prior_turns = opts.priorTurns
  if (opts.opportunityId) body.opportunity_id = opts.opportunityId
  if (opts.documentId) body.document_id = opts.documentId

  let res: Response
  try {
    res = await authFetch('/api/ask/stream', { method: 'POST', body: JSON.stringify(body) })
  } catch {
    return askQuestion(question, opts)
  }
  // A refused request (an attachment that is not ours or has expired, 404; a bad
  // document for an API-key caller, 400) must reach the user as the server's message,
  // not be retried on the plain endpoint, which would refuse it the same way.
  if (res.status === 400 || res.status === 404) {
    throw new Error(`HTTP ${res.status}: ${await readDetail(res)}`)
  }
  if (!res.ok || !res.body) return askQuestion(question, opts)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let final: AskResponse | null = null

  // SSE frames are separated by a blank line; a chunk can split one anywhere, so
  // hold the remainder in `buffer` rather than parsing per chunk.
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''
    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      let evt: AskStreamEvent
      try {
        evt = JSON.parse(line.slice(6))
      } catch {
        continue
      }
      if (evt.type === 'done') final = evt.result
      onEvent(evt)
    }
  }
  if (final) return final
  // Stream ended without a result — do not leave the user with nothing.
  return askQuestion(question, opts)
}

export async function fetchHistory(limit = 20): Promise<HistoryResponse> {
  const res = await authFetch(`/api/history?limit=${limit}`)
  return handleResponse<HistoryResponse>(res)
}

// Fetch a single audit entry with full evidence (tools_called +
// definitions_applied). Backend restricts access to the caller's own
// audits by user_email, so 404 = either not found OR belongs to another
// user; both cases treated the same on the client.
export async function fetchHistoryEntry(auditId: string): Promise<HistoryTurn> {
  const res = await authFetch(`/api/history/${encodeURIComponent(auditId)}`)
  return handleResponse<HistoryTurn>(res)
}

/**
 * Every turn of one past conversation, oldest first (2026-09-30).
 *
 * Used when the sidebar entry carries a conversation_id: the thread is restored whole and
 * the next question CONTINUES it, rather than starting over with one turn as reference.
 * Rows logged before the client sent conversation ids have none and go through
 * fetchHistoryEntry instead. Same ownership rule as the other history endpoints — a
 * 404 is "not yours or not there", and the client treats both the same.
 */
export async function fetchHistoryThread(conversationId: string): Promise<HistoryThreadResponse> {
  const res = await authFetch(`/api/history/thread/${encodeURIComponent(conversationId)}`)
  return handleResponse<HistoryThreadResponse>(res)
}

export async function submitFeedback(
  auditId: string,
  ratingOrPayload: FeedbackRating | FeedbackSubmission,
  comment?: string,
): Promise<{ ok: boolean; status?: string }> {
  // Two call shapes for backwards-compat: legacy (rating, comment) or
  // the enriched payload shape.
  const body: Record<string, unknown> = { audit_id: auditId }
  if (typeof ratingOrPayload === 'string') {
    body.rating = ratingOrPayload
    body.comment = comment || null
  } else {
    Object.assign(body, ratingOrPayload)
  }
  const res = await authFetch('/api/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return handleResponse(res)
}

// --- /api/me ---
export async function fetchMe(): Promise<MeResponse> {
  const res = await authFetch('/api/me')
  return handleResponse<MeResponse>(res)
}

// --- First-demo scorecards (manager-only) ---
export async function fetchScorecards(params: { start?: string; end?: string; ae?: string }): Promise<ScorecardsResponse> {
  const q = new URLSearchParams()
  if (params.start) q.set('start', params.start)
  if (params.end) q.set('end', params.end)
  if (params.ae) q.set('ae', params.ae)
  const res = await authFetch(`/api/scorecards${q.toString() ? `?${q}` : ''}`)
  return handleResponse<ScorecardsResponse>(res)
}

export async function fetchScorecard(callId: string): Promise<ScorecardDetailResponse> {
  const res = await authFetch(`/api/scorecards/${encodeURIComponent(callId)}`)
  return handleResponse<ScorecardDetailResponse>(res)
}

// --- Admin feedback moderation ---
export async function fetchAdminFeedback(
  status?: FeedbackStatus,
  limit = 200,
): Promise<AdminFeedbackListResponse> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  params.set('limit', String(limit))
  const res = await authFetch(`/api/admin/feedback?${params.toString()}`)
  return handleResponse<AdminFeedbackListResponse>(res)
}

export async function patchAdminFeedback(
  feedbackId: number,
  patch: AdminFeedbackPatch,
): Promise<{ ok: boolean; id: number }> {
  const res = await authFetch(`/api/admin/feedback/${feedbackId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  return handleResponse(res)
}

export async function exportAdminFeedback(
  status: FeedbackStatus = 'approved',
): Promise<{ ok: boolean; status: string; count: number; markdown: string }> {
  const res = await authFetch(`/api/admin/feedback/export?status=${status}`)
  return handleResponse(res)
}

// --- Morning Brief ---
// The signed-in user's own demo prep cards for the day. Identity comes from the
// verified Google token server-side, so there is no rep parameter here — a rep can
// only ever fetch their own brief. `rep` is admin-only and used by the preview.
export async function fetchMyBrief(rep?: string): Promise<BriefResponse> {
  const params = new URLSearchParams({ format: 'json' })
  if (rep) params.set('rep', rep)
  const res = await authFetch(`/api/brief/me?${params.toString()}`)
  return handleResponse<BriefResponse>(res)
}

// --- Morning-brief instrumentation (2026-09-12) --------------------------------------
// The brief was live for two weeks with no record of whether it was read or whether a
// line helped. A view is posted once per page load; a vote is posted per line. Both are
// keyed server-side; the client only sends the text it showed.

export async function postBriefView(body: {
  brief_date: string; rep?: string | null; n_demos: number; n_followups: number
}): Promise<void> {
  try {
    await authFetch('/api/brief/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch { /* instrumentation must never break the page */ }
}

export async function postBriefVote(body: BriefVoteBody): Promise<{ ok: boolean; item_hash: string }> {
  const res = await authFetch('/api/brief/vote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse<{ ok: boolean; item_hash: string }>(res)
}

export async function fetchMyBriefVotes(date: string, rep?: string | null): Promise<{ votes: BriefVote[] }> {
  const params = new URLSearchParams({ date })
  if (rep) params.set('rep', rep)
  const res = await authFetch(`/api/brief/votes/me?${params.toString()}`)
  return handleResponse<{ votes: BriefVote[] }>(res)
}

// Admin-only roster for the morning-brief previewer: the reps who have demos on the
// brief's date, with their calendar email (what `?rep=` matches on) and demo count.
export async function fetchBriefReps(): Promise<BriefRepsResponse> {
  const res = await authFetch('/api/brief/reps')
  return handleResponse<BriefRepsResponse>(res)
}


/**
 * Warm one opportunity's context server-side.
 *
 * Called when a rep opens a demo card, not when they ask a question: the retrieval that
 * makes a first question slow runs while they are still reading the card. Returns a small
 * summary — the context itself never comes to the browser.
 *
 * Never throws. Priming is an optimisation, and a chat must open whether or not it
 * succeeded; a failure here just means the first question takes as long as it used to.
 */
export async function primeOpportunity(opportunityId: string): Promise<{
  ok: boolean
  account_name?: string
  n_linked_calls?: number
  approx_tokens?: number
  cached?: boolean
  build_ms?: number
  error?: string
}> {
  try {
    const res = await authFetch('/api/opp/prime', {
      method: 'POST',
      body: JSON.stringify({ opportunity_id: opportunityId }),
    })
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` }
    return await res.json()
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/** Close out (or re-open) one follow-up line — for the signed-in user only. */
export async function postBriefDone(body: {
  account: string | null
  source_date: string
  item_text: string
  done: boolean
}): Promise<{ ok: boolean; item_hash?: string }> {
  const res = await authFetch('/api/brief/done', {
    method: 'POST',
    body: JSON.stringify({ ...body, item_kind: 'owed' }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return await res.json()
}

// --- Per-user context (2026-09-30) ----------------------------------------------------
// Memory, saved workflows and attached documents. Everything here is the signed-in
// user's own; the server keys every row on the verified email and refuses API keys.

export async function fetchMyContext(kind?: UserContextKind): Promise<UserContextListResponse> {
  const params = new URLSearchParams()
  if (kind) params.set('kind', kind)
  const qs = params.toString()
  const res = await authFetch(`/api/me/context${qs ? `?${qs}` : ''}`)
  return handleResponse<UserContextListResponse>(res)
}

export async function upsertMyContext(body: {
  kind: 'memory' | 'workflow'
  body: string
  title?: string
}): Promise<{ ok: boolean; item: UserContextItem }> {
  const res = await authFetch('/api/me/context', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return handleResponse(res)
}

export async function deleteMyContext(kind: UserContextKind, key: string): Promise<void> {
  const res = await authFetch(
    `/api/me/context/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`,
    { method: 'DELETE' },
  )
  await handleResponse<{ ok: boolean }>(res)
}

/**
 * Upload one document to attach to a conversation.
 *
 * Raw bytes with the name in a header, not multipart: the server deliberately has no
 * multipart parser (an undeclared dependency there takes the whole API down at startup).
 * authFetch's JSON Content-Type is overridden here because init.headers merge last.
 * Throws with the server's own message on a refused file (type, size, nothing readable).
 */
export async function uploadDocument(file: File): Promise<UserContextItem> {
  const res = await authFetch('/api/documents', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Filename': encodeURIComponent(file.name),
    },
    body: file,
  })
  const r = await handleResponse<{ ok: boolean; document: UserContextItem }>(res)
  return r.document
}

/**
 * Every follow-up line this user has closed.
 *
 * Not date-scoped on purpose: a line closed last week must stay closed when the follow-up
 * appears on today's brief.
 */
export async function fetchMyBriefDone(): Promise<{ done: BriefDoneItem[] }> {
  try {
    const res = await authFetch('/api/brief/done/me')
    if (!res.ok) return { done: [] }
    return await res.json()
  } catch {
    return { done: [] }   // the brief must render even if this endpoint is unavailable
  }
}
